"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { servicesService } from "@/lib/services/servicesService";

const driverFeatures = [
  {
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
    title: "Management",
    description: "Manage your truck, fleet and schedule with ease from a single dashboard.",
  },
  {
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
    title: "Security",
    description: "Using geolocation, know where your truck, driver and cargo is at all times.",
  },
  {
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
    title: "Messaging",
    description: "Stay in constant communication with your driver via our in-app messaging system.",
  },
];

interface Service {
  id: string;
  title: string;
  slug: string;
  description: string;
  status: 'active' | 'inactive';
  displayOrder: number;
}

export default function ServicesSection() {
  const headingRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible", "animate-fade-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (headingRef?.current) observer?.observe(headingRef?.current);
    if (cardsRef?.current) observer?.observe(cardsRef?.current);
    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    async function fetchServices() {
      try {
        setLoading(true);
        setError(null);
        const data = await servicesService.getAllPublic();
        // Get top 3 services ordered by display_order
        setServices(data.slice(0, 3));
      } catch (err: any) {
        console.error('Error fetching services:', err);
        setError(err.message || 'Failed to load services');
      } finally {
        setLoading(false);
      }
    }

    fetchServices();
  }, []);

  return (
    <section id="services" className="py-16 md:py-28 bg-deep-navy relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-brand-purple/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-brand-green/10 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div ref={headingRef} className="on-scroll text-center mb-12 md:mb-20">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 font-heading font-700 text-xs uppercase tracking-widest px-4 py-2 rounded-full border border-white/10 mb-6 whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-brand-green" />
            Our Services
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-6 whitespace-nowrap">
            Enterprise Class Logistics for the{" "}
            <span className="text-brand-green">Everyday Consumer</span>
          </h2>
          <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Whether you need to move a single large item or manage a fleet of delivery vehicles,
            GoGedit has you covered.
          </p>
        </div>

        {/* Bento Grid */}
        <div ref={cardsRef} className="on-scroll grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Drivers card — large */}
          <div className="bento-card bg-gradient-to-br from-brand-purple to-dark-navy p-6 md:p-8 lg:p-10 lg:row-span-2 flex flex-col justify-between min-h-[380px] lg:min-h-[420px] transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-brand-purple/20">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5 md:mb-6">
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="1" y="3" width="15" height="13" rx="2" />
                  <path d="M16 8h4l3 5v3h-7V8z" />
                  <circle cx="5.5" cy="18.5" r="2.5" />
                  <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
                For our Drivers
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight mb-3 md:mb-4">
                Monetize Your Truck or Fleet
              </h3>
              <p className="text-white/60 leading-relaxed mb-6 md:mb-8 text-sm md:text-base">
                Monetize your truck or fleet on your time and schedule.
                Get connected to paying customers in your area and earn on your terms.
              </p>

              {/* Feature list */}
              <div className="space-y-3 md:space-y-4">
                {driverFeatures?.map((feat) => (
                  <div key={feat?.title} className="flex items-start gap-3 md:gap-4 bg-white/5 rounded-2xl p-3 md:p-4 hover:bg-white/10 transition-colors">
                    <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-white/10 flex items-center justify-center text-white flex-shrink-0">
                      {feat?.icon}
                    </div>
                    <div>
                      <p className="font-bold text-white text-sm mb-1">{feat?.title}</p>
                      <p className="text-white/50 text-xs leading-relaxed">{feat?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Link
              href="/be-a-driver"
              className="mt-6 md:mt-8 inline-flex items-center gap-2 bg-white text-brand-purple font-heading font-700 text-sm px-6 py-3 rounded-full hover:bg-brand-green hover:text-white transition-all w-fit"
            >
              Start Earning Today
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>

          {/* Customers card */}
          <div className="bento-card bg-brand-green/15 border border-brand-green/30 p-6 md:p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-brand-green/20 hover:bg-brand-green/20 hover:border-brand-green/40">
            <div className="inline-flex items-center gap-2 bg-brand-green/20 text-brand-green text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5 md:mb-6">
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              For our Customers
            </div>
            <h3 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight mb-3 md:mb-4">
              Connect, Track, and Deliver
            </h3>
            <p className="text-white/60 leading-relaxed mb-5 md:mb-6 text-sm md:text-base">
              Connect with a driver willing to pickup your large item and deliver it to its
              destination, all while being able to track it in real-time from pickup to drop-off.
            </p>
            <div className="flex flex-wrap gap-2 md:gap-3">
              {["Real-Time GPS", "Secure Payments", "In-App Chat", "Item Insurance"]?.map((tag) => (
                <span
                  key={tag}
                  className="bg-white/10 text-white/80 text-xs font-medium px-3 py-1.5 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Stats card */}
          <div className="bento-card bg-white/5 border border-white/10 p-6 md:p-8 flex items-center gap-4 md:gap-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-white/10 hover:bg-white/10 hover:border-white/20">
            <div className="flex-1 text-center">
              <p className="font-heading text-3xl md:text-4xl font-800 text-brand-green">A→B</p>
              <p className="text-white/40 text-xs uppercase tracking-wider mt-2">Seamless Delivery</p>
            </div>
            <div className="w-px h-12 md:h-16 bg-white/10 flex-shrink-0" />
            <div className="flex-1 text-center">
              <p className="font-heading text-3xl md:text-4xl font-800 text-brand-purple">100%</p>
              <p className="text-white/40 text-xs uppercase tracking-wider mt-2">Real-Time Tracking</p>
            </div>
          </div>
        </div>

        {/* Service Detail Links */}
        <div className="mt-12 md:mt-16">
          <h3 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-white text-center mb-6 md:mb-8">
            Explore Our Services
          </h3>
          
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-center">
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-red-500/20 text-red-400 px-6 py-2 rounded-full hover:bg-red-500/30 transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && services.length === 0 && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
              <p className="text-white/60">No services available at the moment.</p>
            </div>
          )}

          {!loading && !error && services.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {services?.map((service) => (
                <Link
                  key={service?.id}
                  href={`/services/${service?.slug}`}
                  className="bg-white/5 border border-white/10 rounded-2xl p-5 md:p-6 hover:bg-white/10 hover:border-brand-green/40 transition-all duration-300 group"
                >
                  <h4 className="font-heading text-lg md:text-xl font-bold text-white mb-2 group-hover:text-brand-green transition-colors">
                    {service?.title}
                  </h4>
                  <p className="text-white/60 text-sm mb-4">{service?.description}</p>
                  <div className="flex items-center gap-2 text-brand-green text-sm font-medium">
                    Learn More
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="group-hover:translate-x-1 transition-transform">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="text-center mt-10 md:mt-12">
          <p className="text-base md:text-lg text-body-text mb-4">
            Ready to experience hassle-free moving and delivery?
          </p>
          <Link
            href="/be-a-user"
            className="inline-block bg-brand-green text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg font-medium hover:bg-opacity-90 transition-all"
          >
            Sign-Up as a User
          </Link>
        </div>
      </div>
    </section>
  );
}